import React, { useEffect, useState } from 'react';

// The on-site cronjob never stores a reading of the mediablock's clock — a timestamp would be
// up to 15 minutes stale by the time the cloud copy of the organization is refreshed, and the
// cloud cannot reach the player to ask again. It stores the two things that *define* a clock
// instead: the zone the player runs in, and how far its clock sits from true time. Both hold
// between cronjob runs, so this renders a live, second-accurate clock from them without a
// single network call — identically in the cloud app and the on-site one.

// Past this the clock stops being a rounding difference and starts being a fault: KDM validity
// windows and scheduling are both evaluated against the player's own clock.
export const DRIFT_WARNING_SECONDS = 60;

// "2m 16s", "1h 04m", "3d 06h" — a drifted mediablock clock is usually minutes out, so seconds
// stay visible until the drift is large enough that they no longer tell you anything.
export const formatDrift = (seconds) => {
    const total = Math.abs(Math.round(seconds));
    if (total < 60) return `${total}s`;

    const minutes = Math.floor(total / 60);
    if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, '0')}s`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;

    return `${Math.floor(hours / 24)}d ${String(hours % 24).padStart(2, '0')}h`;
};

const ZONE_SOURCE_LABELS = {
    cinema: 'timezone from the cinema record',
    organization: 'timezone from the organization settings',
};

// Intl throws on a zone it does not recognise, which would take the whole panel down with it.
const formatIn = (date, timeZone, options) => {
    try {
        return new Intl.DateTimeFormat(undefined, { ...options, timeZone }).format(date);
    } catch {
        return null;
    }
};

/**
 * The mediablock's own date, time and timezone.
 *
 * @param timeZone            IANA zone the player runs in
 * @param clockOffsetSeconds  player clock minus true time; null when we only know the zone
 * @param timeSource          where the zone came from: 'tms' | 'cinema' | 'organization'
 * @param compact             one line for a panel summary, rather than the full detail block
 */
const MediablockClock = ({ timeZone, clockOffsetSeconds, timeSource, compact = false }) => {
    // These panels sit open on a wall display; without a tick the clock would freeze at
    // whatever it read when the organization was last loaded.
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    if (!timeZone) return null;

    // A null offset means we know where the player is but never got a reading off it, so this
    // is the viewer's own clock rendered in the cinema's zone — honest, but not the player's.
    const isPlayerClock = clockOffsetSeconds !== null && clockOffsetSeconds !== undefined;
    const mediablockNow = new Date(now + (isPlayerClock ? clockOffsetSeconds * 1000 : 0));

    const time = formatIn(mediablockNow, timeZone, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    if (!time) return null;
    const date = formatIn(mediablockNow, timeZone, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

    const hasDrifted = isPlayerClock && Math.abs(clockOffsetSeconds) > DRIFT_WARNING_SECONDS;
    const driftLabel = hasDrifted
        ? `Clock is ${formatDrift(clockOffsetSeconds)} ${clockOffsetSeconds < 0 ? 'behind' : 'ahead of'} real time`
        : null;

    if (compact) {
        return (
            <div className="me-3 text-center text-nowrap">
                <span className="far fa-clock me-2 text-primary" />
                <span>{time}</span>
                <span className="ms-2" style={{ color: '#ffffff80' }}>{timeZone}</span>
                {driftLabel && (
                    <span className="fas fa-triangle-exclamation text-warning ms-2" title={driftLabel} />
                )}
            </div>
        );
    }

    return (
        <div>
            <div>
                <span className="fs-4">{time}</span>
                {date && <span className="ms-2">{date}</span>}
            </div>
            <div style={{ color: '#ffffff80' }}>
                {timeZone}
                {!isPlayerClock && ' — no clock reading from the player'}
                {isPlayerClock && timeSource !== 'tms' && ZONE_SOURCE_LABELS[timeSource] && ` — ${ZONE_SOURCE_LABELS[timeSource]}`}
            </div>
            {driftLabel && (
                <div className="text-warning mt-1">
                    <span className="fas fa-triangle-exclamation me-2" />
                    {driftLabel}
                </div>
            )}
        </div>
    );
};

export default MediablockClock;
